import { createContext, useContext, useEffect, useState } from "react";
import type { SessionUser } from "@lib/schema";
import { fetchSession, logout } from "@lib/api-client";

interface AuthContextState {
  user: SessionUser | null;
  loading: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  login: (redirect?: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setLoading(true);
      const current = await fetchSession();
      setUser(current);
    } catch (error) {
      console.error("Session fetch failed", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleLogin = (redirect = window.location.pathname + window.location.search) => {
    const params = new URLSearchParams({ redirect });
    window.location.href = `/api/auth/google/start?${params.toString()}`;
  };

  const handleLogout = async () => {
    await logout();
    await refresh();
  };

  const isAdmin = Boolean(user?.isAdmin);

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, refresh, login: handleLogin, logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
